import { useState } from "react";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FAQS } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

const CATEGORY_LABELS = {
  ordering: "Ordering & Pricing",
  quality: "Quality & Certifications",
  storage: "Storage & Shelf Life",
  services: "Services",
  delivery: "Delivery & Logistics"
};

export default function FAQ() {
  const [activeTab, setActiveTab] = useState("all");

  const faqs = FAQS;
  const faqCategories = ["all", ...new Set(faqs.map(f => f.category))];
  const filteredFaqs = activeTab === "all" ? faqs : faqs.filter(f => f.category === activeTab);

  return (
    <div className="pt-20" data-testid="faq-page">
      {/* Header */}
      <section className="py-12 md:py-16 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <Badge className="bg-green-100 text-[#2B7A1E] hover:bg-green-100 mb-4 px-4 py-1.5 text-xs font-semibold tracking-wide rounded-full border-0">
            FAQ
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#333] tracking-tight mb-3" style={{ fontFamily: 'Montserrat' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-gray-600 max-w-xl">
            Find quick answers to common questions about our products, ordering, delivery, and services.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-12">
          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-10">
            {faqCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveTab(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${activeTab === cat ? "bg-[#2B7A1E] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                data-testid={`faq-tab-${cat}`}
              >
                {cat === "all" ? "All Questions" : (CATEGORY_LABELS[cat] || cat)}
              </button>
            ))}
          </div>

          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
              {filteredFaqs.map((faq) => (
                <motion.div key={faq.id} variants={fadeUp}>
                  <AccordionItem value={faq.id} className="border border-gray-100 rounded-2xl px-6 data-[state=open]:bg-green-50/50 data-[state=open]:border-green-200 transition-colors">
                    <AccordionTrigger className="text-left text-[#333] font-semibold py-5 hover:no-underline" style={{ fontFamily: 'Montserrat' }} data-testid={`faq-q-${faq.id}`}>
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed pb-5" data-testid={`faq-a-${faq.id}`}>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>

          {/* Still have questions */}
          <div className="mt-14 text-center p-8 bg-[#f8f8f8] rounded-3xl" data-testid="faq-contact-cta">
            <h3 className="text-xl font-bold text-[#333] mb-2" style={{ fontFamily: 'Montserrat' }}>Still have questions?</h3>
            <p className="text-gray-500 text-sm mb-6">Can't find what you're looking for? Our team is happy to help.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact">
                <Button className="rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white font-semibold px-8" data-testid="faq-contact-btn">
                  Contact Us
                </Button>
              </Link>
              <a href="https://wa.me/918308203097?text=Hi%2C%20I%20have%20a%20question%20about%20your%20frozen%20food%20products." target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="rounded-full border-2 border-[#2B7A1E] text-[#2B7A1E] font-semibold px-8 hover:bg-green-50" data-testid="faq-whatsapp-btn">
                  WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
